import { find } from "linkifyjs";
import {
  decodeHtmlEntities,
  formatAnnounceHtmlAsPlainText,
} from "@/features/announce/lib/stripAnnounceHtml";

export type AnnouncementBodyPart =
  | { readonly type: "text"; readonly content: string }
  | {
      readonly type: "link";
      readonly content: string;
      readonly href: string;
      readonly key: string;
    }
  | {
      readonly type: "image";
      readonly src: string;
      readonly alt: string;
      readonly key: string;
    };

const BODY_ELEMENT_PATTERN =
  /<a\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/a\s*>|<img\b((?:[^>"']|"[^"]*"|'[^']*')*)\/?\s*>/gi;
const ATTRIBUTE_PATTERN =
  /(?:^|\s)([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
const MARKER_END = "\uE001";

function safeHttpUrl(value: string): string | null {
  if (!/^https?:\/\//i.test(value) || /\s/.test(value)) {
    return null;
  }
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined && (codePoint < 0x20 || codePoint === 0x7f)) {
      return null;
    }
  }
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname
      ? value
      : null;
  } catch {
    return null;
  }
}

function appendPlainText(
  parts: AnnouncementBodyPart[],
  text: string,
  offset: number,
): void {
  let cursor = 0;
  for (const match of find(text, "url")) {
    const content = match.value.replace(/[。、，．！？]+$/u, "");
    const href = safeHttpUrl(content);
    if (match.start < cursor || !href) continue;
    if (match.start > cursor) {
      parts.push({ type: "text", content: text.slice(cursor, match.start) });
    }
    parts.push({
      type: "link",
      content,
      href,
      key: `url-${offset + match.start}`,
    });
    cursor = match.start + content.length;
  }
  if (cursor < text.length) {
    parts.push({ type: "text", content: text.slice(cursor) });
  }
}

function attributeValue(attributes: string, name: string): string | null {
  for (const attribute of attributes.matchAll(ATTRIBUTE_PATTERN)) {
    if (attribute[1]?.toLowerCase() !== name) continue;
    return decodeHtmlEntities(
      attribute[2] ?? attribute[3] ?? attribute[4] ?? "",
    );
  }
  return null;
}

function anchorHref(attributes: string): string | null {
  const href = attributeValue(attributes, "href");
  return href ? safeHttpUrl(href) : null;
}

function imagePart(attributes: string, index: number): AnnouncementBodyPart {
  const src = attributeValue(attributes, "src");
  const alt = attributeValue(attributes, "alt") ?? "";
  const safeSrc = src ? safeHttpUrl(src) : null;
  return safeSrc
    ? { type: "image", src: safeSrc, alt, key: `image-${index}` }
    : { type: "text", content: alt };
}

function anchorPart(
  attributes: string,
  label: string,
  index: number,
): AnnouncementBodyPart {
  const href = anchorHref(attributes);
  const content = formatAnnounceHtmlAsPlainText(label);
  return href
    ? { type: "link", content, href, key: `anchor-${index}` }
    : { type: "text", content };
}

/** 告知HTMLのリンクと画像だけを取り出し、残りは平文として安全に表示する。 */
export function parseAnnouncementBody(html: string): AnnouncementBodyPart[] {
  let markerStart = "\uE000";
  const decodedHtml = decodeHtmlEntities(html);
  while (html.includes(markerStart) || decodedHtml.includes(markerStart)) {
    markerStart += "\uE000";
  }

  const embeddedParts: AnnouncementBodyPart[] = [];
  let htmlScanOffset = 0;
  let preDepth = 0;
  const updatePreDepth = (end: number): void => {
    for (const tag of html
      .slice(htmlScanOffset, end)
      .matchAll(/<\/?pre\b[^>]*>/gi)) {
      preDepth = tag[0].startsWith("</")
        ? Math.max(0, preDepth - 1)
        : preDepth + 1;
    }
    htmlScanOffset = end;
  };
  const withMarkers = html.replace(
    BODY_ELEMENT_PATTERN,
    (
      _match,
      anchorAttributes: string | undefined,
      label: string | undefined,
      imageAttributes: string | undefined,
      offset: number,
    ) => {
      updatePreDepth(offset);
      const isInsidePre = preDepth > 0;
      const part =
        imageAttributes !== undefined
          ? imagePart(imageAttributes, embeddedParts.length)
          : anchorPart(
              anchorAttributes ?? "",
              label ?? "",
              embeddedParts.length,
            );
      embeddedParts.push(part);
      updatePreDepth(offset + _match.length);
      const marker = `${markerStart}${embeddedParts.length - 1}${MARKER_END}`;
      return isInsidePre ? marker : ` ${marker} `;
    },
  );
  const formatted = formatAnnounceHtmlAsPlainText(withMarkers);
  const parts: AnnouncementBodyPart[] = [];
  let cursor = 0;
  while (cursor < formatted.length) {
    const start = formatted.indexOf(markerStart, cursor);
    if (start < 0) break;
    appendPlainText(parts, formatted.slice(cursor, start), cursor);
    const end = formatted.indexOf(MARKER_END, start + markerStart.length);
    if (end < 0) break;
    const index = Number(formatted.slice(start + markerStart.length, end));
    const embeddedPart = embeddedParts[index];
    if (embeddedPart) parts.push(embeddedPart);
    cursor = end + MARKER_END.length;
  }
  appendPlainText(parts, formatted.slice(cursor), cursor);
  return parts;
}
